import React, { useRef, useEffect, useCallback } from "react";
import { toDateKey } from "./DashboardUtils";
import { ChevronLeft, ChevronRight } from "lucide-react";
import "./DayCarousel.css";

/**
 * Horizontal carousel that shows `cardsPerViewport` DayColumns at a time.
 * Always shows prev/next arrows. When all days fit, arrows jump a full week.
 * On touch devices, swipe navigation also works.
 */
const DayCarousel = ({ days, cardsPerViewport, focusedDate, onFocusChange, onPrev, onNext, children }) => {
    const containerRef = useRef(null);
    const isUserScrolling = useRef(false);
    const scrollTimeout = useRef(null);
    const allVisible = cardsPerViewport >= days.length;

    // Scroll to focused date when it changes externally
    useEffect(() => {
        if (allVisible) return;
        const container = containerRef.current;
        if (!container) return;

        const focusedKey = toDateKey(focusedDate);
        const index = days.findIndex((d) => toDateKey(d) === focusedKey);
        if (index < 0) return;

        const itemWidth = container.scrollWidth / days.length;
        const targetScroll = index * itemWidth;

        if (!isUserScrolling.current) {
            container.scrollTo({ left: targetScroll, behavior: "smooth" });
        }
    }, [focusedDate, days, allVisible]);

    // Detect scroll-snap settle and update focused date
    const handleScroll = useCallback(() => {
        if (allVisible) return;
        isUserScrolling.current = true;
        if (scrollTimeout.current) clearTimeout(scrollTimeout.current);

        scrollTimeout.current = setTimeout(() => {
            isUserScrolling.current = false;
            const container = containerRef.current;
            if (!container || days.length === 0) return;

            const itemWidth = container.scrollWidth / days.length;
            const scrollIndex = Math.round(container.scrollLeft / itemWidth);
            const clampedIndex = Math.max(0, Math.min(scrollIndex, days.length - 1));

            const newFocused = days[clampedIndex];
            if (newFocused && toDateKey(newFocused) !== toDateKey(focusedDate)) {
                onFocusChange(newFocused);
            }
        }, 150);
    }, [days, focusedDate, onFocusChange, allVisible]);

    // Keyboard navigation
    useEffect(() => {
        const handleKeyDown = (e) => {
            if (e.target.tagName === "INPUT" || e.target.tagName === "TEXTAREA" || e.target.tagName === "SELECT") return;
            if (e.key === "ArrowLeft") { e.preventDefault(); onPrev(); }
            if (e.key === "ArrowRight") { e.preventDefault(); onNext(); }
        };
        window.addEventListener("keydown", handleKeyDown);
        return () => window.removeEventListener("keydown", handleKeyDown);
    }, [onPrev, onNext]);

    useEffect(() => {
        return () => {
            if (scrollTimeout.current) clearTimeout(scrollTimeout.current);
        };
    }, []);

    const cssVars = { "--cards-per-viewport": cardsPerViewport };
    const carouselClass = allVisible ? "day-carousel day-carousel--full" : "day-carousel";

    return (
        <div className="day-carousel-wrapper">
            <button className="day-carousel-arrow day-carousel-arrow--left" onClick={onPrev} title="Vorheriger Tag">
                <ChevronLeft size={20} />
            </button>
            <div
                className={carouselClass}
                ref={allVisible ? undefined : containerRef}
                onScroll={allVisible ? undefined : handleScroll}
                style={cssVars}
            >
                {React.Children.map(children, (child, i) => (
                    <div className="day-carousel-item" key={i}>
                        {child}
                    </div>
                ))}
            </div>
            <button className="day-carousel-arrow day-carousel-arrow--right" onClick={onNext} title="Nächster Tag">
                <ChevronRight size={20} />
            </button>
        </div>
    );
};

export default DayCarousel;
